// Centralized API client -- replaces all direct localStorage reads/writes
// with real calls to the RyzenOS backend.

const API_BASE = '/api';

function getToken() {
  return localStorage.getItem('ryzenos_token');
}

function setToken(token) {
  if (token) localStorage.setItem('ryzenos_token', token);
  else localStorage.removeItem('ryzenos_token');
}

async function request(path, { method = 'GET', body, headers = {} } = {}) {
  const token = getToken();
  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);

  const res = await fetch(`${API_BASE}${path}`, opts);

  if (res.status === 401) {
    // Token invalid/expired -- force logout
    setToken(null);
    sessionStorage.removeItem('ryzenos_session');
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  let data;
  const text = await res.text();
  try { data = text ? JSON.parse(text) : null; } catch { data = null; }

  if (!res.ok) {
    throw new Error(data?.error || `Request failed: ${res.status}`);
  }
  return data;
}

const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: 'POST', body }),
  patch: (path, body) => request(path, { method: 'PATCH', body }),
  delete: (path) => request(path, { method: 'DELETE' }),
  getToken,
  setToken,

  /* ── Auth ── */
  auth: {
    login: (username, password) => request('/auth/login', { method: 'POST', body: { username, password } }),
    activate: (payload) => request('/auth/activate', { method: 'POST', body: payload }),
    me: () => request('/auth/me'),
    changePassword: (newPassword) => request('/auth/change-password', { method: 'POST', body: { newPassword } }),
    resetPassword: (userId, newPassword) => request(`/auth/reset-password/${userId}`, { method: 'POST', body: { newPassword } }),
    resetWithKey: (key, username, newPassword) => request('/auth/reset-with-key', { method: 'POST', body: { key, username, newPassword } }),
    heartbeat: (seconds = 60) => request('/auth/heartbeat', { method: 'POST', body: { seconds } }),
  },

  /* ── Team ── */
  team: {
    list: () => request('/team'),
    add: (member) => request('/team', { method: 'POST', body: member }),
    update: (id, fields) => request(`/team/${id}`, { method: 'PATCH', body: fields }),
    remove: (id) => request(`/team/${id}`, { method: 'DELETE' }),
  },

  /* ── Leads ── */
  leads: {
    list: () => request('/leads'),
    add: (lead) => request('/leads', { method: 'POST', body: lead }),
    update: (id, fields) => request(`/leads/${id}`, { method: 'PATCH', body: fields }),
    remove: (id) => request(`/leads/${id}`, { method: 'DELETE' }),
    convert: (id) => request(`/leads/${id}/convert`, { method: 'POST' }),
  },

  /* ── Clients ── */
  clients: {
    list: () => request('/clients'),
    add: (client) => request('/clients', { method: 'POST', body: client }),
    update: (id, fields) => request(`/clients/${id}`, { method: 'PATCH', body: fields }),
    remove: (id) => request(`/clients/${id}`, { method: 'DELETE' }),
  },

  /* ── Finance ── */
  finance: {
    invoices: {
      list: () => request('/finance/invoices'),
      add: (inv) => request('/finance/invoices', { method: 'POST', body: inv }),
      update: (id, fields) => request(`/finance/invoices/${id}`, { method: 'PATCH', body: fields }),
      remove: (id) => request(`/finance/invoices/${id}`, { method: 'DELETE' }),
      markPaid: (id) => request(`/finance/invoices/${id}/mark-paid`, { method: 'POST' }),
    },
    expenses: {
      list: () => request('/finance/expenses'),
      add: (exp) => request('/finance/expenses', { method: 'POST', body: exp }),
      update: (id, fields) => request(`/finance/expenses/${id}`, { method: 'PATCH', body: fields }),
      remove: (id) => request(`/finance/expenses/${id}`, { method: 'DELETE' }),
    },
  },

  /* ── Projects ── */
  projects: {
    list: () => request('/projects'),
    add: (p) => request('/projects', { method: 'POST', body: p }),
    update: (id, fields) => request(`/projects/${id}`, { method: 'PATCH', body: fields }),
    remove: (id) => request(`/projects/${id}`, { method: 'DELETE' }),
  },

  /* ── Calendar ── */
  calendar: {
    list: () => request('/calendar'),
    add: (ev) => request('/calendar', { method: 'POST', body: ev }),
    remove: (id) => request(`/calendar/${id}`, { method: 'DELETE' }),
  },

  /* ── Notifications ── */
  notifications: {
    list: () => request('/notifications'),
    create: (n) => request('/notifications', { method: 'POST', body: n }),
    markRead: (id) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
    markAllRead: () => request('/notifications/read-all', { method: 'PATCH' }),
  },

  /* ── Attendance ── */
  attendance: {
    get: (date) => request(`/attendance/${date}`),
    save: (date, entries) => request(`/attendance/${date}`, { method: 'PUT', body: { entries } }),
  },

  /* ── Leave ── */
  leave: {
    list: () => request('/leave'),
    apply: (data) => request('/leave', { method: 'POST', body: data }),
    update: (id, status) => request(`/leave/${id}`, { method: 'PATCH', body: { status } }),
  },

  /* ── Backup ── */
  backup: {
    exportUrl: () => `${API_BASE}/backup/export`,
    list: () => request('/backup/list'),
  },

  /* ── Billing (plan info; backend endpoint arrives with multi-tenancy) ── */
  billing: {
    get: () => request('/billing'),
    requestUpgrade: (targetPlan) => request('/billing/upgrade-request', { method: 'POST', body: { targetPlan } }),
  },
};

export default api;
